import { motion } from "framer-motion";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { MapPin } from "lucide-react";

const GEO_URL = "/assets/india.json";

const STATE_CODES = {
  Odisha: "OD", Karnataka: "KA", Maharashtra: "MH", Delhi: "DL", "Uttar Pradesh": "UP",
  Chhattisgarh: "CG", Assam: "AS", "Tamil Nadu": "TN", Jharkhand: "JH",
  "West Bengal": "WB", Bihar: "BR", Gujarat: "GJ", Punjab: "PB", Haryana: "HR",
};

const FOOTPRINT_STATES = new Set([
  "Odisha", "Karnataka", "Maharashtra", "Delhi", "Uttar Pradesh", "Chhattisgarh",
  "Assam", "Tamil Nadu", "Jharkhand", "West Bengal", "Bihar", "Gujarat", "Punjab", "Haryana",
]);

const OFFICES = [
  { name: "Bhubaneswar", label: "Corporate Office", coordinates: [85.8245, 20.2961] },
  { name: "Balasore", label: "Registered Office", coordinates: [86.9, 21.4942] },
  { name: "Ghaziabad", label: "Branch Office", coordinates: [77.4538, 28.6692] },
];

export default function Presence() {
  return (
    <section id="presence" className="py-28 lg:py-40 border-t border-white/10 overflow-hidden" data-testid="presence-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="font-mono-accent text-xs tracking-[0.35em] uppercase text-neon mb-6"
        >
          Presence
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-20"
          data-testid="presence-heading"
        >
          A pan-India <span className="text-outline">footprint.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 relative"
          >
            <div className="absolute inset-0 bg-electric/10 blur-[120px] rounded-full pointer-events-none" />
            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 950, center: [80.5, 22.5] }}
              width={720}
              height={760}
              style={{ width: "100%", height: "auto" }}
              data-testid="india-map"
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies
                    .filter((geo) => geo.properties.district === undefined)
                    .map((geo) => {
                    const state = geo.properties.st_nm;
                    const active = FOOTPRINT_STATES.has(state);
                    return (
                      <g key={geo.rsmKey}>
                        <Geography
                          geography={geo}
                          style={{
                            default: {
                              fill: active ? "#0055FF" : "#0D1733",
                              fillOpacity: active ? 0.75 : 1,
                              stroke: active ? "#00E5FF" : "rgba(226,232,240,0.28)",
                              strokeWidth: active ? 1 : 0.7,
                              outline: "none",
                            },
                            hover: {
                              fill: active ? "#3377FF" : "#16224A",
                              fillOpacity: active ? 0.95 : 1,
                              stroke: active ? "#00E5FF" : "rgba(226,232,240,0.45)",
                              strokeWidth: active ? 1.2 : 0.8,
                              outline: "none",
                            },
                            pressed: { outline: "none" },
                          }}
                        />
                        {active && (
                          <Marker coordinates={geoCentroid(geo)}>
                            <text
                              textAnchor="middle"
                              style={{
                                fontFamily: "Space Mono, monospace",
                                fontSize: 12,
                                fontWeight: "bold",
                                fill: "#FFFFFF",
                                stroke: "#030712",
                                strokeWidth: 3,
                                paintOrder: "stroke",
                                letterSpacing: "0.12em",
                                pointerEvents: "none",
                              }}
                            >
                              {STATE_CODES[state]}
                            </text>
                          </Marker>
                        )}
                      </g>
                    );
                  })
                }
              </Geographies>
              {OFFICES.map((office, i) => (
                <Marker key={office.name} coordinates={office.coordinates} data-testid={`office-marker-${i}`}>
                  <motion.circle
                    fill="none"
                    stroke="#00E5FF"
                    strokeWidth={1.5}
                    initial={{ r: 6, opacity: 0.7 }}
                    animate={{ r: [6, 18], opacity: [0.7, 0] }}
                    transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.5, ease: "easeOut" }}
                  />
                  <circle r={4.5} fill="#00E5FF" stroke="#030712" strokeWidth={1.5} />
                  <text
                    textAnchor="start"
                    x={10}
                    y={4}
                    style={{ fontFamily: "Space Mono, monospace", fontSize: 11, fill: "#F8FAFC", letterSpacing: "0.1em" }}
                  >
                    {office.name.toUpperCase()}
                  </text>
                </Marker>
              ))}
            </ComposableMap>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-5">
              Offices
            </p>
            <div className="space-y-4 mb-12">
              {OFFICES.map((office, i) => (
                <div key={office.name} className="flex items-center gap-4 border border-white/10 bg-navy-800/40 px-5 py-4 card-glow" data-testid={`presence-office-${i}`}>
                  <span className="w-2.5 h-2.5 rounded-full bg-neon shadow-[0_0_12px_#00E5FF]" />
                  <div>
                    <p className="font-display font-bold text-sm uppercase tracking-wide">{office.name}</p>
                    <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-slate-500">{office.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <p className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-5 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-electric" /> Project Footprint — 14 States
            </p>
            <div className="flex flex-wrap gap-2" data-testid="footprint-states">
              {Array.from(FOOTPRINT_STATES).map((state) => (
                <span
                  key={state}
                  className="font-mono-accent text-[10px] tracking-[0.15em] uppercase text-slate-300 border border-electric/30 bg-electric/10 px-3 py-1.5 hover:bg-electric/25 hover:text-white transition-colors duration-300"
                >
                  {state}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
